import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, ListGroup, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faDownload,
  faFileExclamation,
  faInfoCircle,
  faMessage,
  faSpinner
} from '@fortawesome/pro-solid-svg-icons';

import ListItem from 'components/file_system/list_item';
import FileViewer from './file_viewer';
import VaultAccess from './vault_access';
import EmptyState from './empty_state';
import RequestMoreInfo from './request_more_info';

import { fetchNavigation, handleNavigation, previewFile } from '../actions';
import { setShowLogin, setShowInfoRequest } from '../actions/ui';
import classNames from 'classnames';
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { faArrowPointer } from '@fortawesome/pro-regular-svg-icons';
import { formatContainerClasses } from '../helpers/card_component_container_class';
import ChangeDocAccessPortal from './vault_steps/preview_portals/change_doc_access_portal';
import { PROFILE_OPTIONS } from '../actions/preview';

const CONTENT_TO_SHOW = {
  REQUEST_MORE_INFO: 'request_more_info',
  VAULT_ACCESS: 'vault_access',
  FILE: 'file',
  EMPTY_STATE: 'empty_state'
};

export function DocumentNavigation({
  currentFolder,
  downloadAllUrl,
  fetchNavigation,
  file,
  fileNavigationContainerSelector,
  handleNavigation,
  hasVaultGates,
  navigation,
  profile,
  previewFile,
  readonly,
  recaptchaKey,
  setShowLogin,
  showRequestMoreInfo,
  setShowInfoRequest,
  showLogin
}) {
  useEffect(() => {
    if (!navigation) fetchNavigation();
  }, [navigation]);

  if (!navigation) {
    return (
      <div className="text-center my-5 text-muted">
        <FontAwesomeIcon icon={faSpinner} size="2x" spin/>
      </div>
    );
  }

  const vaultAccessComplete = (result) => {
    setShowLogin(false);
    fetchNavigation(result?.onlyGetUnlockedFile || false);
  };

  const isDocsPresent = (folder) => {
    return folder.children.some((child) => {
      if (child.folder) {
        return isDocsPresent(child);
      }

      return true;
    });
  };

  if (!isDocsPresent(navigation[0])) {
    let ChildComponent = (
      <React.Fragment>
        <div className="my-auto">
          <EmptyState
            cardClasses="connect-card"
            header="There are no documents available here."
            icon={faFileExclamation}
          />
        </div>
        <div className="mt-auto"/>
      </React.Fragment>
    );


    if (showLogin) {
      ChildComponent = <VaultAccess loginOnly={showLogin} noDocs onRefreshNavigation={vaultAccessComplete}/>;
    } else if (hasVaultGates) {
      ChildComponent = <VaultAccess loginOnly={false} noDocs onRefreshNavigation={vaultAccessComplete}/>;
    }

    return (
      <div className={formatContainerClasses}>
        {ChildComponent}
      </div>
    );
  }

  const parentFolder = navigation[navigation.length - 2] || navigation[0];
  const keyForFile = file => file.folder ? `folder${file.id}` : `file${file.id}`;

  const renderRow = (item, idx) => {
    const tooltip = <Tooltip id={`list-item-${item.id}`}>{item.name}</Tooltip>;
    const selected = file == item;

    const wrapper = (
      <ListGroup.Item
        action
        active={selected}
        className={classNames('rounded-3 border-0 py-3 ', { 'bg-branding': selected })}
      />
    );

    return (
      <OverlayTrigger delay={{ show: 500, hide: 100 }} key={item.id} overlay={tooltip} placement="top">
        <ListItem
          file={item}
          index={idx}
          key={keyForFile(item)}
          selected={selected}
          wrapper={wrapper}
          onNavigation={handleNavigation}
          onPreview={previewFile}
        />
      </OverlayTrigger>
    );
  };

  const showVaultLogin = !showRequestMoreInfo && (showLogin || (file && file.locked));
  const unlockedChildren = currentFolder.children.filter(({ locked }) => !locked);
  const gainApprovalChild = currentFolder.children.find(({ accessIfApproved }) => accessIfApproved);

  let contentToShow = CONTENT_TO_SHOW.EMPTY_STATE;
  if (showRequestMoreInfo) {
    contentToShow = CONTENT_TO_SHOW.REQUEST_MORE_INFO;
  } else if (showVaultLogin) {
    contentToShow = CONTENT_TO_SHOW.VAULT_ACCESS;
  } else if (file) {
    contentToShow = CONTENT_TO_SHOW.FILE;
  }

  function handlePreviewAccessChange(_, __, updatedProfile) {
    fetchNavigation(false, updatedProfile);
  }

  const showBottomPortal = contentToShow !== CONTENT_TO_SHOW.VAULT_ACCESS && readonly &&
    !!profile[PROFILE_OPTIONS.ACCESS_LEVEL] && !hasVaultGates;

  const downloadAllTooltip = (
    <Tooltip id="download-all-tooltip">This downloads all documents you have access to</Tooltip>
  );

  return (
    <React.Fragment>
      {ReactDOM.createPortal((
        <Button disabled={showRequestMoreInfo} name="request-more-info-button" onClick={() => setShowInfoRequest(true)}>
          <FontAwesomeIcon className="me-2" icon={faMessage}/>
          Contact Broker
        </Button>
      ), document.querySelector('#js-request-more-info-button'))}

      {ReactDOM.createPortal(
        (
          <React.Fragment>
            <div>
              <div className="d-flex px-3 align-items-center mb-1">
                <span className="clickable flex-grow-1" onClick={() => handleNavigation(parentFolder)}>
                  <strong>
                    {navigation.length > 1 && <FontAwesomeIcon className="me-2" icon={faChevronLeft}/>}
                    {parentFolder.name}
                  </strong>
                </span>
              </div>
              <ListGroup variant="flush">
                {unlockedChildren.map(renderRow)}
              </ListGroup>
              {gainApprovalChild && (
                <div className="my-4">
                  <Button
                    className="w-100"
                    variant="outline-primary"
                    onClick={() => previewFile(gainApprovalChild)}
                  >
                    <FontAwesomeIcon className="me-2" icon={faArrowPointer}/>
                    <strong>Access secure documents</strong>
                  </Button>
                </div>
              )}
            </div>
            {downloadAllUrl && (
              <div className="border-top py-3 mt-3">
                <a
                  className="btn btnl-link text-body text-branding-hover ps-0 fw-normal"
                  href={downloadAllUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FontAwesomeIcon className="fa-fw" icon={faDownload} size="lg"/>
                  <span>Download All</span>
                  <OverlayTrigger overlay={downloadAllTooltip}>
                    <FontAwesomeIcon icon={faInfoCircle} size="xs"/>
                  </OverlayTrigger>
                </a>
              </div>
            )}
          </React.Fragment>
        ), document.querySelector(fileNavigationContainerSelector)
      )}
      {contentToShow === CONTENT_TO_SHOW.REQUEST_MORE_INFO && (
        <div className={formatContainerClasses} name="request-more-info-container">
          <RequestMoreInfo recaptchaKey={recaptchaKey}/>
        </div>
      )}
      {contentToShow === CONTENT_TO_SHOW.VAULT_ACCESS && (
        <div className={formatContainerClasses}>
          <VaultAccess loginOnly={showLogin} onRefreshNavigation={vaultAccessComplete}/>
        </div>
      )}
      {contentToShow === CONTENT_TO_SHOW.FILE && (
        <div name="file-viewer-container">
          <FileViewer file={file} higherFloatingButton={showBottomPortal} previewProfile={profile} readonly={readonly}/>
        </div>
      )}
      {contentToShow === CONTENT_TO_SHOW.EMPTY_STATE && (
        <div className={formatContainerClasses}>
          <div className="my-auto">
            <EmptyState cardClasses="connect-card" header="Select a folder to view documents." icon={faFolderOpen}/>
          </div>
          <div className="mt-auto"/>
        </div>
      )}
      {showBottomPortal && (<ChangeDocAccessPortal onNext={handlePreviewAccessChange}/>)}
    </React.Fragment>
  );
}

DocumentNavigation.propTypes = {
  currentFolder: PropTypes.shape({
    children: PropTypes.array.isRequired
  }),
  downloadAllUrl: PropTypes.string,
  fetchNavigation: PropTypes.func.isRequired,
  file: PropTypes.shape({
    folder: PropTypes.bool,
    locked: PropTypes.bool,
    id: PropTypes.number.isRequired
  }),
  fileNavigationContainerSelector: PropTypes.string.isRequired,
  handleNavigation: PropTypes.func.isRequired,
  hasVaultGates: PropTypes.bool.isRequired,
  navigation: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.arrayOf(PropTypes.shape({
      accessIfApproved: PropTypes.bool.isRequired,
      accessLevel: PropTypes.number.isRequired,
      docVaultEntryId: PropTypes.number.isRequired,
      folder: PropTypes.bool.isRequired,
      icon: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      locked: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      pendingApproval: PropTypes.bool.isRequired,
      updatedAt: PropTypes.string.isRequired,
      updatedAtLabel: PropTypes.string.isRequired,
      url: PropTypes.string
    })),
    folder: PropTypes.bool.isRequired,
    icon: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  previewFile: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  readonly: PropTypes.bool.isRequired,
  recaptchaKey: PropTypes.string.isRequired,
  setShowInfoRequest: PropTypes.func.isRequired,
  setShowLogin: PropTypes.func.isRequired,
  showLogin: PropTypes.bool.isRequired,
  showRequestMoreInfo: PropTypes.bool.isRequired
};

export const mapStateToProps = ({ navigation, file, preview, ui, vault: { hasVaultGates } }) => {
  return {
    currentFolder: navigation ? navigation[navigation.length - 1] : null,
    downloadAllUrl: ui.downloadAllUrl,
    hasVaultGates,
    navigation,
    file,
    profile: preview?.profile || {},
    readonly: preview.readonly,
    showLogin: ui.showLogin,
    showRequestMoreInfo: ui.showRequestMoreInfo
  };
};

const mapDispatchToProps = { handleNavigation, previewFile, fetchNavigation, setShowLogin, setShowInfoRequest };

export default connect(mapStateToProps, mapDispatchToProps)(DocumentNavigation);
